import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import {
  Home, ScrollText, User, CreditCard, FileBarChart, Mail, Gift, Sparkles, BookOpen,
  LogOut, ExternalLink, Leaf, Flower2, ListChecks, Menu, X, Globe, Scale, ShoppingBag,
} from "lucide-react";
import customerApi from "./customerApi";
import { VerifyEmailBanner } from "./VerifyEmailBanner";
import { useLang } from "../i18n/LanguageContext";
import { HQ_NAV } from "./hqNav";

/* Customer HQ Layout , lightened "stained-glass flatlay" rebuild (Jun 27 2026).
   Heritage HQ now floats translucent cream panels over a soft mosaic
   background. Sidebar uses a softer lavender ink; HQ seal hero logo
   replaces the old serif text mark.
   Feb 2026: added olive theme toggle (persisted per browser) so male
   customers can swap the girly lilac banner for a garden olive. */

const THEMES = {
  lilac: {
    sidebarBg:      "#7C5E8A",
    sidebarEdge:    "#5A4565",
    seal:           "/photos/hq/hq-seal-olive.png",
    overlayA:       "rgba(251,246,233,0.55)",
    overlayB:       "rgba(251,246,233,0.55)",
  },
  olive: {
    sidebarBg:      "#5A6B3E",
    sidebarEdge:    "#3E4A2A",
    seal:           "/photos/hq/hq-seal-purple.png",
    overlayA:       "rgba(245,244,230,0.55)",
    overlayB:       "rgba(245,244,230,0.55)",
  },
};

const LAVENDER       = "#7C5E8A";   // softer sidebar ink (was deep aubergine)
const LAVENDER_DEEP  = "#5A4565";   // sidebar edge
const PAPER_TINT     = "rgba(251, 246, 233, 0.92)";  // floating panel cream
const PARCH_EDGE     = "#EFE4CC";
const GOLD           = "#C9A268";
const BG_TILE        = "/photos/hq/hq-stained-glass-bg.png";
const HQ_SEAL        = "/photos/hq/heritage-hq-seal.png";

const THEME_VARS = {
  lilac: { "--hq-accent": "#6F4A7A", "--hq-deep": "#5A4565", "--hq-soft": "#F2EAF3", "--hq-soft-edge": "#E1D3E4" },
  olive: { "--hq-accent": "#55682F", "--hq-deep": "#3E4A2A", "--hq-soft": "#ECF0DF", "--hq-soft-edge": "#D6DFC0" },
};

export default function HQLayout() {
  const [me, setMe] = useState(null);
  const [selfAuditOn, setSelfAuditOn] = useState(null);
  const [priority, setPriority] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [themeKey, setThemeKey] = useState(() => {
    try { return localStorage.getItem("heritage-hq-theme") === "olive" ? "olive" : "lilac"; }
    catch { return "lilac"; }
  });
  const theme = THEMES[themeKey] || THEMES.lilac;
  const navigate = useNavigate();
  const { lang, setLang, t } = useLang();
  const toggleLang = () => setLang(lang === "en" ? "mi" : "en");

  const toggleTheme = () => {
    const next = themeKey === "lilac" ? "olive" : "lilac";
    setThemeKey(next);
    try { localStorage.setItem("heritage-hq-theme", next); } catch { /* ignore */ }
  };

  useEffect(() => {
    customerApi.get("/customer/me").then((r) => setMe(r.data)).catch(() => {});
    customerApi.get("/customer/self-audit/enabled")
      .then((r) => setSelfAuditOn(!!r.data?.enabled))
      .catch(() => setSelfAuditOn(false));
    customerApi.get("/customer/priority/status")
      .then((r) => setPriority(r.data))
      .catch(() => setPriority({ is_priority: false }));
  }, []);

  const nav = HQ_NAV
    .filter((item) => item.slug !== "customer-self-audit" || selfAuditOn === true)
    .filter((item) => !item.priorityOnly || priority?.is_priority);

  const logout = async () => {
    try { await customerApi.post("/customer/logout"); } catch { /* clear the local session regardless */ }
    localStorage.removeItem("heritage-customer-token");
    localStorage.removeItem("heritage-customer-name");
    localStorage.removeItem("heritage-customer-email");
    window.dispatchEvent(new Event("heritage-customer-session"));
    navigate("/account/login");
  };

  return (
    <div
      className={`min-h-screen flex relative hq-theme-${themeKey}`}
      style={{
        // Stained-glass flatlay background , tile it for seamless coverage,
        // soft cream overlay keeps panels readable.
        backgroundImage: `linear-gradient(${theme.overlayA}, ${theme.overlayB}), url(${BG_TILE})`,
        backgroundSize: "auto, 600px auto",
        backgroundRepeat: "no-repeat, repeat",
        backgroundAttachment: "fixed",
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        ...(THEME_VARS[themeKey] || THEME_VARS.lilac),
      }}
      data-testid="hq-layout"
    >
      {/* Mobile header + slide-in menu (phones only) */}
      <div
        className="md:hidden fixed top-0 inset-x-0 z-[60] flex items-center justify-between px-4 py-2.5 text-white shadow-lg"
        style={{ backgroundColor: theme.sidebarBg, borderBottom: `1px solid ${theme.sidebarEdge}` }}
        data-testid="hq-mobile-header"
      >
        <Link to="/account" className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
          <img src={theme.seal} alt="Heritage HQ" className="w-9 h-9 object-contain drop-shadow" />
          <span className="text-[10px] tracking-[0.35em] uppercase font-semibold">Heritage HQ</span>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/dahlias/store" data-testid="hq-mobile-return-store" aria-label="Return to store"
            className="p-2 rounded-md bg-white/15 hover:bg-white/25 transition-colors">
            <ShoppingBag className="w-5 h-5" />
          </Link>
          <button
            type="button"
            onClick={toggleTheme}
            aria-pressed={themeKey === "olive"}
            title={themeKey === "lilac" ? "Switch to olive garden theme" : "Switch back to lilac"}
            data-testid="hq-mobile-header-theme-toggle"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-white/25 bg-white/10 hover:bg-white/20 transition-colors"
          >
            {themeKey === "lilac" ? <Flower2 className="w-4 h-4" /> : <Leaf className="w-4 h-4" />}
            <span
              aria-hidden="true"
              className="relative inline-block w-7 h-3.5 rounded-full transition-colors"
              style={{ backgroundColor: themeKey === "olive" ? "#C9DB9F" : "#E8D6F0" }}
            >
              <span
                className="absolute top-0.5 w-2.5 h-2.5 rounded-full bg-white shadow transition-all"
                style={{ left: themeKey === "olive" ? "1rem" : "0.125rem" }}
              />
            </span>
          </button>
          <button
            type="button"
            onClick={(event) => { event.stopPropagation(); setMobileOpen((value) => !value); }}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="hq-mobile-menu-panel"
            data-testid="hq-mobile-menu-toggle"
            className="p-2 rounded-md bg-white/15 hover:bg-white/25"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div
        id="hq-mobile-menu-panel"
        className={`md:hidden fixed inset-0 z-50 transition-opacity duration-200 ${mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        data-testid="hq-mobile-menu"
        data-state={mobileOpen ? "open" : "closed"}
        aria-hidden={!mobileOpen}
      >
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <nav
            className={`absolute top-[60px] inset-x-3 rounded-lg shadow-2xl p-4 space-y-0.5 text-sm text-white max-h-[calc(100vh-76px)] overflow-y-auto transition-transform duration-200 ${mobileOpen ? "translate-y-0" : "-translate-y-2"}`}
            style={{ backgroundColor: theme.sidebarBg, border: `1px solid ${theme.sidebarEdge}` }}
          >
            {me && (
              <div className="flex items-center gap-2.5 px-3 pb-3 mb-2 border-b border-white/20" data-testid="hq-mobile-identity">
                <span className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 bg-white/85" style={{ border: "2px solid #C9A268" }}>
                  <img
                    src={me.profile_picture || theme.seal}
                    alt={me.name || "Profile"}
                    className={me.profile_picture ? "w-full h-full object-cover" : "w-full h-full object-contain p-0.5"}
                  />
                </span>
                <span className="text-[10px] tracking-[0.25em] uppercase opacity-90 truncate">
                  {me.name || me.email}
                </span>
              </div>
            )}
            {nav.map((item) => (item.external ? (
              <a
                key={item.slug}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`hq-mobile-nav-${item.slug}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-md text-white/85 hover:bg-white/10"
              >
                <item.Icon className="w-4 h-4 opacity-90" /> {t(item.titleKey)} <ExternalLink className="w-3 h-3 opacity-70" />
              </a>
            ) : (
              <NavLink
                key={item.slug}
                to={item.to}
                end={item.end}
                onClick={() => setMobileOpen(false)}
                data-testid={`hq-mobile-nav-${item.slug}`}
                className={({ isActive }) =>
                  "flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors " +
                  (isActive ? "bg-white/20 font-semibold" : "text-white/85 hover:bg-white/10")
                }
              >
                <item.Icon className="w-4 h-4 flex-shrink-0 opacity-90" />
                {t(item.titleKey)}
              </NavLink>
            )))}
            <Link to="/dahlias/store" onClick={() => setMobileOpen(false)} data-testid="hq-mobile-menu-return-store"
              className="flex items-center gap-3 px-3 py-2.5 rounded-md text-white font-semibold bg-white/10 hover:bg-white/20">
              <ShoppingBag className="w-4 h-4" /> Return to Store
            </Link>
            <div className="pt-3 mt-2 border-t border-white/20 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={toggleLang}
                data-testid="hq-mobile-lang-toggle"
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-white/25 bg-white/10 text-[10px] tracking-[0.25em] uppercase font-semibold"
              >
                <Globe className="w-3.5 h-3.5" /> {lang === "en" ? "Te Reo" : "English"}
              </button>
              <button
                type="button"
                onClick={toggleTheme}
                data-testid="hq-mobile-theme-toggle"
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-white/25 bg-white/10 text-[10px] tracking-[0.25em] uppercase font-semibold"
              >
                {themeKey === "lilac" ? <><Flower2 className="w-3.5 h-3.5" /> Lilac</> : <><Leaf className="w-3.5 h-3.5" /> Olive</>}
              </button>
              <button
                type="button"
                onClick={logout}
                data-testid="hq-mobile-logout"
                className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full text-[10px] tracking-[0.25em] uppercase font-semibold border"
                style={{ borderColor: "#F4E7C9", color: "#F4E7C9" }}
              >
                <LogOut className="w-3 h-3" /> {t("hq.signOut")}
              </button>
            </div>
          </nav>
      </div>

      {/* Sidebar (floating, themed) */}
      <aside
        data-testid="hq-sidebar"
        className="hidden md:flex md:w-[240px] lg:w-[260px] flex-col flex-shrink-0 px-5 py-7 text-white m-3 lg:m-5 rounded-lg shadow-xl"
        style={{
          backgroundColor: theme.sidebarBg,
          border: `1px solid ${theme.sidebarEdge}`,
          backdropFilter: "blur(8px)",
        }}
      >
        <Link to="/account" className="block text-center mb-5">
          <img
            src={theme.seal}
            alt="Heritage Headquarters"
            className="w-28 h-28 mx-auto mb-2 object-contain drop-shadow"
          />
          <div className="text-[9px] tracking-[0.45em] uppercase opacity-90">
            {t("hq.portal")}
          </div>
        </Link>

        {/* Theme toggle , lilac ↔ olive (persists per browser) */}
        <button
          type="button"
          onClick={toggleTheme}
          data-testid="hq-theme-toggle"
          aria-pressed={themeKey === "olive"}
          title={themeKey === "lilac" ? "Switch to olive garden theme" : "Switch back to lilac"}
          className="mb-5 w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-full border border-white/25 bg-white/10 hover:bg-white/20 transition-colors text-[10px] tracking-[0.3em] uppercase font-semibold"
        >
          <span className="inline-flex items-center gap-1.5">
            {themeKey === "lilac"
              ? <><Flower2 className="w-3.5 h-3.5" /> Lilac</>
              : <><Leaf className="w-3.5 h-3.5" /> Olive</>}
          </span>
          <span
            aria-hidden="true"
            className="relative inline-block w-8 h-4 rounded-full transition-colors"
            style={{ backgroundColor: themeKey === "olive" ? "#C9DB9F" : "#E8D6F0" }}
          >
            <span
              className="absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all"
              style={{ left: themeKey === "olive" ? "1.125rem" : "0.125rem" }}
            />
          </span>
        </button>

        {/* Language toggle , English ↔ Te Reo Māori (persists per browser) */}
        <button
          type="button"
          onClick={toggleLang}
          data-testid="hq-lang-toggle"
          aria-pressed={lang === "mi"}
          title={lang === "en" ? "Huri ki te Reo Māori" : "Switch back to English"}
          className="-mt-2 mb-5 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full border border-white/25 bg-white/10 hover:bg-white/20 transition-colors text-[10px] tracking-[0.3em] uppercase font-semibold"
        >
          <Globe className="w-3.5 h-3.5" /> {lang === "en" ? "Te Reo Māori" : "English"}
        </button>

        <nav className="flex-1 space-y-0.5 text-sm">
          {nav.map((item) => (item.external ? (
            <a
              key={item.slug}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`hq-nav-${item.slug}`}
              className="flex items-center gap-2.5 px-3 py-2 mt-1 rounded-md text-white/85 hover:bg-white/10 hover:text-white transition-colors"
            >
              <item.Icon className="w-4 h-4 flex-shrink-0 opacity-90" />
              <span className="flex-1">{t(item.titleKey)}</span>
              <ExternalLink className="w-3 h-3 opacity-70" />
            </a>
          ) : (
            <NavLink
              key={item.slug}
              to={item.to}
              end={item.end}
              data-testid={`hq-nav-${item.slug}`}
              className={({ isActive }) =>
                "flex items-center gap-2.5 px-3 py-2 rounded-md transition-colors " +
                (isActive
                  ? "bg-white/20 text-white font-semibold"
                  : "text-white/85 hover:bg-white/10 hover:text-white")
              }
            >
              <item.Icon className="w-4 h-4 flex-shrink-0 opacity-90" />
              <span className="flex-1">{t(item.titleKey)}</span>
            </NavLink>
          )))}
        </nav>

        <div className="pt-5 mt-6 border-t border-white/20 space-y-2">
          {me && (
            <div className="flex items-center justify-center gap-2.5 mb-2" data-testid="hq-sidebar-identity">
              <span className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 bg-white/85" style={{ border: "2px solid #C9A268" }}>
                <img
                  src={me.profile_picture || theme.seal}
                  alt={me.name || "Profile"}
                  className={me.profile_picture ? "w-full h-full object-cover" : "w-full h-full object-contain p-0.5"}
                />
              </span>
              <span className="text-[10px] tracking-[0.25em] uppercase opacity-85 truncate">
                {me.name || me.email}
              </span>
            </div>
          )}
          <Link to="/dahlias/store" data-testid="hq-return-store"
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-sm text-[10px] tracking-[0.25em] uppercase font-semibold transition border border-white/40 bg-white/10 hover:bg-white/20 text-white">
            <ShoppingBag className="w-3.5 h-3.5" /> Return to Store
          </Link>
          <button
            type="button"
            onClick={logout}
            data-testid="hq-logout"
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-sm text-[10px] tracking-[0.25em] uppercase font-semibold transition border"
            style={{ borderColor: "#F4E7C9", color: "#F4E7C9" }}
          >
            <LogOut className="w-3 h-3" /> {t("hq.signOut")}
          </button>
          <div className="text-[10px] tracking-[0.35em] uppercase opacity-70 text-center pt-2">
            ✦ Est · 1932 ✦
          </div>
        </div>
      </aside>

      {/* Main , floating panels over the stained-glass flatlay */}
      <main className="flex-1 min-w-0 pt-[52px] md:pt-0">
        <div className="p-3 md:p-5 lg:p-8">
          <div
            className="rounded-lg shadow-xl p-5 md:p-8 lg:p-10 min-h-[calc(100vh-2rem)]"
            style={{
              backgroundColor: PAPER_TINT,
              border: `1px solid ${PARCH_EDGE}`,
              backdropFilter: "blur(6px)",
            }}
            data-testid="hq-content-panel"
          >
            <VerifyEmailBanner
              me={me}
              onVerified={() => customerApi.get("/customer/me").then((r) => setMe(r.data)).catch(() => {})}
            />
            {priority?.is_priority && (
              <div className="mb-5 p-4 md:p-5 rounded-md border-l-4 flex flex-wrap items-center justify-between gap-3"
                style={{ backgroundColor: "#F2EAF3", borderLeftColor: "#6E1E3A", border: `1px solid ${PARCH_EDGE}`, borderLeftWidth: 4 }}
                data-testid="hq-priority-banner">
                <div style={{ color: "#5A4565" }}>
                  <div className="font-serif text-2xl">Your order is in our Priority Queue</div>
                  <p className="text-sm italic opacity-80">Please review and confirm your Heritage order before packing.</p>
                </div>
                <Link to="/account/priority-order" data-testid="hq-priority-banner-cta"
                  className="h-12 px-6 bg-[#6E1E3A] text-white text-xs font-semibold tracking-[0.14em] uppercase inline-flex items-center gap-2">
                  <Flower2 className="w-4 h-4" /> Review Priority Order
                  {priority.unread > 0 && <span className="ml-1 px-2 py-0.5 rounded-full bg-white text-[#6E1E3A] text-[10px] font-bold">{priority.unread} new</span>}
                </Link>
              </div>
            )}
            <Outlet context={{ me, selfAuditEnabled: selfAuditOn, priority }} />
          </div>
        </div>
      </main>
    </div>
  );
}

export { LAVENDER, LAVENDER_DEEP, PAPER_TINT, PARCH_EDGE, GOLD };
